import { useMemo, useState, useEffect } from 'react'

// Components
import TextInput from 'components/molecules/TextInput/TextInput'
import Autocomplete from '@mui/material/Autocomplete'
import { FormControl, Typography, IconButton } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Edit } from '@mui/icons-material'
import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import { LoadingButton } from 'components/mui'
import { CustomModal } from 'components/molecules/Modal/Modal'

// Services
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import {
  useAddReferringPhysician,
  useEditReferringPhysician,
  useGetReferringPhysician,
} from 'lib/apollo/hooks'

// Types
import type {
  DynamicFormValues,
  ReportsDynamicFormProps,
  FormField,
} from './types'
import { ReferralPhysicianAndSurgeonRequirementEnum } from 'lib/services/api/email-service/getOrganizationReports.ts/types'
import type { SurgeryReportField } from 'lib/services/api/email-service/getOrganizationReports.ts/types'
import {
  Option,
  ReferringPhysician,
  CustomChangeEvent,
  EPhysiciansTitle,
} from 'common/types'
import {
  physicianDetailsFormSchema,
  ModalProps,
} from '../ImplantReportSender.types'

// Utils
import dayjs from 'lib/dayjs'
import { getZodError } from 'common/utils'
import { enqueueSnackbar } from 'notistack'

// Styles
import styles from './ReportsDynamicForm.module.scss'

const useReportsDynamicFormLogic = ({
  template,
  provider,
}: ReportsDynamicFormProps) => {
  const requirementType = template?.referralPhysicianAndSurgeonRequirement
  const isSurgeonRequired =
    requirementType ===
    ReferralPhysicianAndSurgeonRequirementEnum.SURGEON_REQUIRED
  const isReferralPhysicianRequired =
    requirementType ===
    ReferralPhysicianAndSurgeonRequirementEnum.REFERRAL_PHYSICIAN_REQUIRED
  const isPhysicianRequired = isSurgeonRequired || isReferralPhysicianRequired

  const [selectedPhysician, setSelectedPhysician] = useState<
    Option | undefined
  >(undefined)
  const [search, setSearch] = useState('')
  const [physicianDetails, setPhysicianDetails] = useState<ReferringPhysician>({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
  })
  const [openModal, setOpenModal] = useState<ModalProps>({ open: false })
  const [updatedPhysicianDetails, setUpdatedPhysicianDetails] =
    useState<ReferringPhysician>({
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
    })
  const [formErrors, setFormErrors] = useState<Partial<ReferringPhysician>>()

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<DynamicFormValues>({
    mode: 'onChange',
    defaultValues: {
      physicianId: isSurgeonRequired && provider?._id ? provider._id : null,
    },
  })

  // Services
  const { physiciansTitle } = useOrganizationSettings()
  const { data: physicianData, loading: physicianLoading } =
    useGetReferringPhysician({
      lastName: search && isPhysicianRequired ? search : undefined,
      firstName: search && isPhysicianRequired ? undefined : undefined,
    })

  const [addReferringPhysician, { loading: isAdding }] =
    useAddReferringPhysician()
  const [editReferringPhysician, { loading: isEditing }] =
    useEditReferringPhysician()

  const formDef = useMemo(() => {
    if (!template) return null

    return {
      allowSaveDraft: true,
      submitButtonText: 'Submit Report',
      showProgress: false,
      sections: template.sections.map((section) => ({
        id: section.id,
        title: section.title,
        order: section.order,
        description: undefined,
        fields: section.fields,
      })),
    }
  }, [template])

  const physicians = useMemo(() => {
    if (!isPhysicianRequired) return []
    if (physiciansTitle === EPhysiciansTitle.ReferringPhysicians) {
      return search ? physicianData?.getPhysician || [] : []
    }
    return physicianData?.getPhysician || []
  }, [physicianData, search, physiciansTitle, isPhysicianRequired])

  const lastNameOptions: Option[] = physicians.map((item) => ({
    name: `${item.lastName}, ${item.firstName}|${item.email}`,
    id: item._id as string,
  }))

  const modalHeader =
    openModal.type === 'create'
      ? isSurgeonRequired
        ? 'Add Surgeon'
        : physiciansTitle === EPhysiciansTitle.Physicians
        ? 'Add Physician'
        : 'Add Referring Physician'
      : isSurgeonRequired
      ? 'Edit Surgeon'
      : physiciansTitle === EPhysiciansTitle.Physicians
      ? 'Edit Physician'
      : 'Edit Referring Physician'

  useEffect(() => {
    if (isSurgeonRequired && provider) {
      setPhysicianDetails({
        firstName: provider.firstName || '',
        lastName: provider.lastName || '',
        email: provider.emailAddresses?.[0] || '',
        confirmEmail: provider.emailAddresses?.[0] || '',
      })
      setSelectedPhysician({
        id: provider._id || '',
        name: `${provider.firstName} ${provider.lastName}`,
      })
      setValue('physicianId', provider._id || '')
    }
  }, [isSurgeonRequired, provider, setValue])

  const formatListValue = (
    value: any,
    listConfig?: FormField['listConfig']
  ) => {
    if (!listConfig || !Array.isArray(value)) return value
    return value.join(listConfig.separator)
  }

  const parseListValue = (
    value: string,
    listConfig?: FormField['listConfig']
  ) => {
    if (!listConfig || !value) return value
    return value
      .split(listConfig.separator)
      .map((item) => item.trim())
      .filter(Boolean)
  }

  const renderField = (field: SurgeryReportField) => {
    const starLabel = field.required ? `${field.label}*` : field.label
    const fieldError = (errors as any)?.[field.name]
    const requiredRuleMessage = `${field.label} cannot be empty`
    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf }) => {
              const displayValue = field.listConfig
                ? formatListValue(rhf.value, field.listConfig)
                : rhf.value ?? ''

              const handleChange = (e: any) => {
                const inputValue = e.target?.value || e.target.value
                if (field.listConfig) {
                  const parsedValue = parseListValue(
                    inputValue,
                    field.listConfig
                  )
                  rhf.onChange(parsedValue)
                } else {
                  rhf.onChange(inputValue)
                }
              }

              const placeholder = field.listConfig
                ? `${field.label || ''} (separated by "${
                    field.listConfig?.separator
                  }")`.trim()
                : field.label || ''

              return (
                <TextInput
                  id={field.id}
                  label={starLabel}
                  placeholder={placeholder}
                  variant="outlined"
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={displayValue}
                  handleChangeFormData={handleChange}
                  fullwidth
                  shrink={true}
                  size="small"
                  className={styles.textInput}
                  error={!!fieldError}
                  helperText={fieldError?.message as string}
                />
              )
            }}
          />
        )
      case 'list':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf }) => (
              <TextInput
                id={field.id}
                label={starLabel}
                placeholder={field.label || ''}
                variant="outlined"
                type="text"
                value={rhf.value ?? ''}
                handleChangeFormData={(e) =>
                  rhf.onChange((e as any).target?.value)
                }
                fullwidth
                shrink={true}
                size="small"
                className={styles.textInput}
                error={!!fieldError}
                helperText={fieldError?.message as string}
              />
            )}
          />
        )
      case 'phone':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf }) => (
              <TextInput
                id={field.id}
                label={starLabel}
                placeholder={field.label || ''}
                variant="outlined"
                type="tel"
                value={rhf.value ?? ''}
                handleChangeFormData={(e) =>
                  rhf.onChange((e as any).target?.value)
                }
                fullwidth
                shrink={true}
                size="small"
                className={styles.textInput}
                error={!!fieldError}
                helperText={fieldError?.message as string}
              />
            )}
          />
        )

      case 'textarea':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf }) => (
              <TextInput
                id={field.id}
                label={starLabel}
                placeholder={field.label || ''}
                value={rhf.value || ''}
                handleChangeFormData={(e) => rhf.onChange(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                size="small"
                className={styles.textAreaField}
                error={!!fieldError}
                helperText={fieldError?.message as string}
                variant="outlined"
              />
            )}
          />
        )

      case 'dropdown': {
        const options = field.dropdownOptions || []
        // const isMulti = field.type === VariableTypeEnum.DROPDOWN
        const isMulti = false
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? {
                    validate: (v: any) =>
                      Array.isArray(v)
                        ? v.length > 0 || requiredRuleMessage
                        : !!v || requiredRuleMessage,
                  }
                : undefined
            }
            render={({ field: rhf }) => (
              <FormControl fullWidth>
                {isMulti ? (
                  <Autocomplete
                    id={field.id}
                    multiple
                    options={options}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.label === v.label}
                    filterSelectedOptions
                    value={
                      Array.isArray(rhf.value)
                        ? options.filter((o) =>
                            (rhf.value as string[]).includes(o.label)
                          )
                        : []
                    }
                    onChange={(_, selected) =>
                      rhf.onChange(
                        (selected as Array<{ label: string }>).map(
                          (s) => s.label
                        )
                      )
                    }
                    renderInput={(params) => (
                      <TextInput
                        {...params}
                        label={starLabel}
                        placeholder={field.label || ''}
                        size="small"
                        className={styles.autocompleteField}
                        error={!!fieldError}
                        helperText={fieldError?.message as string}
                        variant="outlined"
                      />
                    )}
                  />
                ) : (
                  <Autocomplete
                    id={field.id}
                    options={options}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.label === v.label}
                    value={
                      rhf.value
                        ? options.find((o) => o.label === rhf.value) || null
                        : null
                    }
                    onChange={(_, selected) =>
                      rhf.onChange(
                        (selected as { label: string } | null)?.label ?? ''
                      )
                    }
                    renderInput={(params) => (
                      <TextInput
                        {...params}
                        sx={{
                          margin: '0',
                        }}
                        label={starLabel}
                        placeholder={field.label || ''}
                        size="small"
                        className={styles.autocompleteField}
                        error={!!fieldError}
                        helperText={fieldError?.message as string}
                        variant="outlined"
                      />
                    )}
                  />
                )}
              </FormControl>
            )}
          />
        )
      }

      // case 'radio':
      //   return (
      //     <Controller
      //       key={field.id}
      //       name={field.name}
      //       control={control}
      //       rules={
      //         field.required
      //           ? { required: { value: true, message: requiredRuleMessage } }
      //           : undefined
      //       }
      //       render={({ field: rhf }) => (
      //         <FormControl error={!!fieldError}>
      //           <FormLabel>{starLabel}</FormLabel>
      //           <RadioGroup {...rhf} row>
      //             {(field.dropdownOptions || []).map((opt) => (
      //               <FormControlLabel
      //                 key={opt.label}
      //                 value={opt.label}
      //                 control={<Radio />}
      //                 label={opt.label}
      //               />
      //             ))}
      //           </RadioGroup>
      //           {fieldError?.message && (
      //             <FormHelperText>
      //               {fieldError.message as string}
      //             </FormHelperText>
      //           )}
      //         </FormControl>
      //       )}
      //     />
      //   )

      // case 'checkbox':
      //   return (
      //     <Controller
      //       key={field.id}
      //       name={field.name}
      //       control={control}
      //       rules={
      //         field.required
      //           ? { validate: (v: any) => !!v || requiredRuleMessage }
      //           : undefined
      //       }
      //       render={({ field: rhf }) => (
      //         <FormControl error={!!fieldError}>
      //           <FormControlLabel
      //             control={<Checkbox {...rhf} checked={!!rhf.value} />}
      //             label={starLabel}
      //           />
      //           {fieldError?.message && (
      //             <FormHelperText>
      //               {fieldError.message as string}
      //             </FormHelperText>
      //           )}
      //         </FormControl>
      //       )}
      //     />
      //   )

      case 'date':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf, ...rest }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={starLabel}
                  value={rhf.value ? dayjs(rhf.value) : null}
                  onChange={(data: dayjs.Dayjs | null) => {
                    if (data) {
                      rhf.onChange(
                        new Date(data.format('YYYY-MM-DD')).toISOString()
                      )
                    } else {
                      rhf.onChange(null)
                    }
                  }}
                  openTo="day"
                  slotProps={{
                    textField: {
                      className: styles.dateField,
                      error: !!fieldError,
                      helperText: fieldError?.message as string,
                      variant: 'outlined',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                  {...rest}
                />
              </LocalizationProvider>
            )}
          />
        )

      case 'time':
        return (
          <Controller
            key={field.id}
            name={field.name}
            control={control}
            rules={
              field.required
                ? { required: { value: true, message: requiredRuleMessage } }
                : undefined
            }
            render={({ field: rhf }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  ampm
                  label={starLabel}
                  value={rhf.value ? dayjs(rhf.value) : null}
                  onChange={(v) => rhf.onChange(v?.toISOString() ?? null)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      className: styles.timeField,
                      size: 'small',
                      error: !!fieldError,
                      helperText: fieldError?.message as string,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />
        )
      default:
        return null
    }
  }

  const handleSelectPhysician = (e: CustomChangeEvent) => {
    const value = e.target.value as Option
    setSelectedPhysician({
      ...value,
      name: value.name
        .split(',')[0]
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' '),
    })

    const selected = physicians.find((item) => item._id === value.id)

    if (selected) {
      setPhysicianDetails(selected)
      setValue('physicianId', selected._id as string)
    }
  }

  const handleEditClick = () => {
    setOpenModal({ open: true, type: 'edit' })
    if (physicianDetails) {
      setUpdatedPhysicianDetails({
        ...physicianDetails,
        confirmEmail: physicianDetails.email,
      })
    }
  }

  const handleAddPhysicianClick = () => {
    setOpenModal({
      open: true,
      type: 'create',
    })
    setUpdatedPhysicianDetails({
      lastName: search,
      firstName: '',
      email: '',
      confirmEmail: '',
    })
  }

  const handleCloseModal = () => {
    setOpenModal({ open: false })
    setUpdatedPhysicianDetails({
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
    })
    setFormErrors({})
  }

  const handleChangeUpdatedPhysicianDetails = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormErrors((prev) => ({ ...prev, [e.target.name]: '' }))
    setUpdatedPhysicianDetails((prev) => {
      if (e.target.name === 'email' || e.target.name === 'confirmEmail') {
        return {
          ...prev,
          [e.target.name]: e.target.value.toLowerCase(),
        }
      }
      return {
        ...prev,
        [e.target.name]: e.target.value,
      }
    })
  }

  const handleAddPhysician = async () => {
    const result = physicianDetailsFormSchema.safeParse(updatedPhysicianDetails)
    if (!result.success) {
      setFormErrors(getZodError(result.error))
      return
    }

    const { _id, email, firstName, lastName } = updatedPhysicianDetails

    if (openModal.type === 'create') {
      addReferringPhysician({
        variables: {
          physician: {
            email,
            lastName,
            firstName,
          },
        },
      })
        .then((res) => {
          if (res.data?.createPhysician) {
            const result = res.data?.createPhysician
            handleSuccess(result)
          }
        })
        .catch((err: Error) => {
          enqueueSnackbar(err.message, {
            variant: 'error',
          })
        })
    }
    if (openModal.type === 'edit' && _id) {
      editReferringPhysician({
        variables: {
          physician: {
            _id,
            email,
            lastName,
            firstName,
          },
        },
      })
        .then((res) => {
          if (res.data?.editPhysician) {
            const result = res.data?.editPhysician
            handleSuccess(result)
          }
        })
        .catch((err) => {
          enqueueSnackbar(err.message, {
            variant: 'error',
          })
        })
    }
  }

  const handleSuccess = (result: ReferringPhysician) => {
    const action = openModal.type
    const message = `Physician ${
      action === 'create' ? 'added' : 'updated'
    } successfully.`

    setPhysicianDetails(result)
    setUpdatedPhysicianDetails({
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
    })
    setSelectedPhysician({
      id: result._id as string,
      name: result.lastName
        .split(',')[0]
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(' '),
    })
    setValue('physicianId', result._id as string)
    setOpenModal({ open: false })
    enqueueSnackbar(message, {
      variant: 'success',
    })
  }

  const renderPhysicianField = () => {
    if (!isPhysicianRequired) return null

    return (
      <div className={styles.physicianSection}>
        <Controller
          name="physicianId"
          control={control}
          rules={{
            required: isPhysicianRequired
              ? `${
                  isSurgeonRequired ? 'Surgeon' : 'Physician'
                } selection is required`
              : false,
          }}
          render={({ field, fieldState }) => (
            <>
              <AutocompleteInput
                size="small"
                id="physicianLastName"
                label={
                  isSurgeonRequired
                    ? 'Surgeon Last Name'
                    : 'Physician Last Name'
                }
                name="physicianLastName"
                variant="outlined"
                value={selectedPhysician || null}
                onTextInputChange={(e) => setSearch(e.target.value as string)}
                handleChangeFormData={handleSelectPhysician}
                fullWidth
                autoSelect={false}
                showEmail
                options={lastNameOptions}
                isLoading={physicianLoading}
                addOption
                disableClearable
                error={!!fieldState.error}
                noOptionsText={
                  lastNameOptions.length === 0 &&
                  search.length > 1 &&
                  !physicianLoading ? (
                    <div className="implant-report-header-no-options">
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        No options found
                      </Typography>
                      <LoadingButton onClick={handleAddPhysicianClick}>
                        {isSurgeonRequired
                          ? 'Add Surgeon'
                          : physiciansTitle ===
                            EPhysiciansTitle.ReferringPhysicians
                          ? 'Add Referring Physician'
                          : 'Add Physician'}
                      </LoadingButton>
                    </div>
                  ) : isSurgeonRequired ? (
                    'Please enter a surgeon last name to begin search'
                  ) : (
                    'Please enter a physician last name to begin search'
                  )
                }
              />
              {fieldState.error && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {fieldState.error.message}
                </Typography>
              )}
            </>
          )}
        />
        <div className={styles.physicianEmailContainer}>
          <TextInput
            id="physicianEmail"
            label={isSurgeonRequired ? 'Surgeon Email' : 'Physician Email'}
            variant="outlined"
            value={physicianDetails?.email}
            fullWidth
            disabled
            size="small"
            sx={{ mt: 0 }}
          />
          {physicianDetails?.email &&
            physicianDetails.email !== provider?.emailAddresses?.[0] && (
              <IconButton onClick={handleEditClick}>
                <Edit />
              </IconButton>
            )}
        </div>
      </div>
    )
  }

  const renderPhysicianModal = () => {
    if (!isPhysicianRequired) return null

    return (
      <CustomModal
        open={openModal.open}
        handleOnSubmit={() => {
          handleAddPhysician()
        }}
        icon={<></>}
        header={modalHeader}
        isLoadingState={isAdding || isEditing}
        secondaryButtonAction={handleCloseModal}
      >
        <TextInput
          disabled={isAdding || isEditing}
          id="referringFirstName"
          label="Physician First Name"
          name="firstName"
          variant="outlined"
          value={updatedPhysicianDetails?.firstName}
          fullWidth
          handleChangeFormData={handleChangeUpdatedPhysicianDetails}
          errorMessage={formErrors?.firstName || ''}
          error={!!formErrors?.firstName}
        />

        <TextInput
          disabled={isAdding || isEditing}
          id="referringLastName"
          label="Physician Last Name"
          name="lastName"
          variant="outlined"
          value={updatedPhysicianDetails?.lastName}
          fullWidth
          handleChangeFormData={handleChangeUpdatedPhysicianDetails}
          errorMessage={formErrors?.lastName || ''}
          error={!!formErrors?.lastName}
        />

        <TextInput
          disabled={isAdding || isEditing}
          id="referringEmail"
          label="Physician Email"
          name="email"
          variant="outlined"
          value={updatedPhysicianDetails?.email}
          handleChangeFormData={handleChangeUpdatedPhysicianDetails}
          fullWidth
          validateEmail
          errorMessage={formErrors?.email || ''}
          error={!!formErrors?.email}
          onPaste={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
        />

        {openModal.type === 'edit'
          ? updatedPhysicianDetails?.email !== physicianDetails?.email && (
              <TextInput
                disabled={isAdding || isEditing}
                id="confirmEmail"
                label="Confirm Email"
                name="confirmEmail"
                variant="outlined"
                value={updatedPhysicianDetails?.confirmEmail}
                handleChangeFormData={handleChangeUpdatedPhysicianDetails}
                fullWidth
                validateEmail
                errorMessage={formErrors?.confirmEmail || ''}
                error={!!formErrors?.confirmEmail}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            )
          : updatedPhysicianDetails?.email && (
              <TextInput
                disabled={isAdding || isEditing}
                id="confirmEmail"
                label="Confirm Email"
                name="confirmEmail"
                variant="outlined"
                value={updatedPhysicianDetails.confirmEmail}
                handleChangeFormData={handleChangeUpdatedPhysicianDetails}
                fullWidth
                validateEmail
                errorMessage={formErrors?.confirmEmail || ''}
                error={!!formErrors?.confirmEmail}
                onPaste={(e) => e.preventDefault()}
                onCopy={(e) => e.preventDefault()}
              />
            )}
      </CustomModal>
    )
  }

  return {
    renderField,
    formDef,
    control,
    handleSubmit,
    errors,
    renderPhysicianField,
    renderPhysicianModal,
    isPhysicianRequired,
  }
}

export default useReportsDynamicFormLogic
